/** The plan ladder (#313) — every priced tier for the member's network, the
 *  member's own marked, with monthly prices. Renders on the Connect tab beside
 *  the speed already shown, NOT next to the capacity card: the capacity spec
 *  decided the upgrade prompt appears only on amber/red so it never reads as a
 *  permanent upsell banner, and an always-present ladder there is that banner.
 *
 *  Prices go to EVERYONE; the Switch buttons only to tenant admins — the same
 *  split Connect already uses for the Wi-Fi password. Like `capacity-card.tsx`
 *  the card keeps its place when it has nothing to say, rather than silently
 *  not being there. */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowUp, Zap } from "lucide-react";
import { Anchor, Badge, Box, Button, Card, Divider, Group, Modal, Stack, Text, Tooltip } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuthStore } from "@/lib/auth";
import { tenantApi } from "@/lib/api/client";
import type { NetworkPlanError, NetworkPlanTier } from "@/lib/api/client";
import {
  NETWORK_CAPACITY_QUERY_KEY,
  NETWORK_INFO_QUERY_KEY,
  NETWORK_PLAN_QUERY_KEY,
  useNetworkPlan,
} from "@/hooks/use-network-queries";
import { supportLink } from "./shared";

/** A Switch clicked before billing details exist parks its target tier here,
 *  survives the detour to Settings, and completes on return — the member's
 *  intent is not lost to the form they were sent through. Session-scoped on
 *  purpose: a tier id from last week should not spring a confirmation. */
export const PENDING_PLAN_CHANGE_KEY = "nodus.pendingPlanChange";

const priceLabel = (tier: NetworkPlanTier) =>
  // 0 renders "Included", never "0 kr" — 50/50 is in the rent. Null and zero
  // are different answers (see frontend speed-limit-columns.tsx); null-priced
  // tiers never reach this card because the backend excludes them.
  tier.price_sek === 0 ? "Included" : `${tier.price_sek.toLocaleString("sv-SE")} kr/mo`;

const speedLabel = (tier: NetworkPlanTier) => `${tier.download_mbps} / ${tier.upload_mbps} Mbit/s`;

const askLink = supportLink(
  "About our connection",
  "Hi! We have a question about our network plan.",
);

function TierSpeed({ tier, current }: { tier: NetworkPlanTier; current: boolean }) {
  return (
    <Group gap={8} wrap="nowrap" style={{ overflow: "hidden" }}>
      <Group gap={2} wrap="nowrap">
        <ArrowDown size={12} style={{ color: "var(--mantine-primary-color-filled)", flexShrink: 0 }} />
        <ArrowUp size={12} style={{ color: "var(--mantine-color-green-6)", flexShrink: 0 }} />
      </Group>
      <Text
        fz="sm"
        fw={current ? 600 : 400}
        style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
      >
        {speedLabel(tier)}
      </Text>
    </Group>
  );
}

export function PlanCard({ hasNetwork = true }: { hasNetwork?: boolean }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data, isLoading } = useNetworkPlan();
  // Reflow on the CARD's width, not the viewport's — the card is already
  // narrow inside a two-column grid on a laptop. Below ~400px the three
  // columns cannot hold a speed, a control and a price without the speed
  // overrunning into the button, so each tier becomes two rows instead.
  const { ref, width } = useElementSize();
  const narrow = width > 0 && width < 400;

  const [confirmTier, setConfirmTier] = useState<NetworkPlanTier | null>(null);
  const [billingDetourTier, setBillingDetourTier] = useState<NetworkPlanTier | null>(null);

  const tiers = data?.tiers ?? [];
  const currentIndex = useMemo(
    () => tiers.findIndex((t) => t.id === data?.current_tier_id),
    [tiers, data?.current_tier_id],
  );
  // Two different ways to have no row highlighted, and they must not read
  // alike. `current_tier_id` null is an honest "we don't know" — nobody
  // ever assigned this network a tier. A NON-null current_tier_id whose
  // `current_download_mbps` is null is the Unlimited tier (crm.models
  // NetworkSpeedLimit with upload/download NULL) — an operator's deliberate
  // choice, unpriced and hidden from this very ladder (speed_tiers_for
  // excludes unpriced rows), so it can never appear as one of `tiers`.
  const isUnconfigured = !!data && data.current_tier_id == null;
  const isUnlimitedTier = !!data && data.current_tier_id != null && data.current_download_mbps == null;
  // Both read as "everything below is a downgrade" for the Switch-button
  // math — moving off Unlimited is exactly as much a downgrade as moving
  // off any real tier, and an unconfigured network gets the same cautious
  // treatment since we cannot prove it is slower than the ladder either.
  const isUncapped = isUnconfigured || isUnlimitedTier;
  // The backend names the DATE a downgrade unlocks; the button copy counts
  // days because "in 3 days" reads better than a date on a small control.
  const cooldownDays = useMemo(() => {
    if (!data?.downgrade_available_on) return 0;
    const unlock = new Date(`${data.downgrade_available_on}T00:00:00`);
    return Math.max(0, Math.ceil((unlock.getTime() - Date.now()) / 86_400_000));
  }, [data?.downgrade_available_on]);

  const changeMutation = useMutation({
    mutationFn: (tier: NetworkPlanTier) => tenantApi.updateNetworkPlan(tier.id),
    onSuccess: (_res, tier) => {
      toast.success(`Plan changed to ${speedLabel(tier)}`);
      setConfirmTier(null);
      queryClient.invalidateQueries({ queryKey: NETWORK_PLAN_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NETWORK_INFO_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: NETWORK_CAPACITY_QUERY_KEY });
    },
    onError: (err: unknown, tier) => {
      const body = (err as { response?: { data?: NetworkPlanError } })?.response?.data;
      setConfirmTier(null);
      // The billing gate is the ONE machine-readable refusal (an exact token,
      // schemas.py's contract); every other gate sends prose to show as-is.
      if (body?.detail === "billing_details_required") {
        // Not a failure — the missing prerequisite opens its own form and
        // the intent survives the detour (see PENDING_PLAN_CHANGE_KEY).
        sessionStorage.setItem(PENDING_PLAN_CHANGE_KEY, String(tier.id));
        setBillingDetourTier(tier);
        return;
      }
      toast.error(body?.detail || "Couldn't change the plan. Please try again.");
    },
  });

  const startSwitch = (tier: NetworkPlanTier) => {
    if (data && !data.billing_details_complete) {
      // Open the billing form rather than letting the PUT fail — same gate,
      // caught one step earlier so the member never sees an error first.
      sessionStorage.setItem(PENDING_PLAN_CHANGE_KEY, String(tier.id));
      setBillingDetourTier(tier);
      return;
    }
    setConfirmTier(tier);
  };

  // Returning from the billing detour: the parked intent re-opens the
  // confirmation (never the PUT itself — a change still gets confirmed).
  useEffect(() => {
    if (!data?.can_change || !data.billing_details_complete) return;
    const pending = sessionStorage.getItem(PENDING_PLAN_CHANGE_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_PLAN_CHANGE_KEY);
    const tier = data.tiers.find((t) => t.id === Number(pending));
    if (tier && tier.id !== data.current_tier_id) setConfirmTier(tier);
  }, [data]);

  const header = (
    <Group gap="xs" mb="sm">
      <Zap size={20} style={{ color: "var(--mantine-primary-color-filled)" }} />
      <Text fw={500}>Your plan</Text>
    </Group>
  );

  const footer = (
    <>
      <Divider mt="md" mb="sm" />
      <Text fz="xs" c="dimmed">
        Monthly, excluding VAT. We record the date a plan changes; your invoice is handled separately.{" "}
        <Anchor component={Link} to={askLink} fz="xs">
          Ask about your connection →
        </Anchor>
      </Text>
    </>
  );

  if (isLoading) {
    return (
      <Card withBorder padding="lg" ref={ref}>
        {header}
        <Text fz="sm" c="dimmed" ta="center" py="xs">Loading…</Text>
      </Card>
    );
  }

  // No data, or no priced tiers to show. Both mean "nothing to compare", and
  // the card keeps its place and says so — the capacity card's lesson.
  if (!data || tiers.length === 0) {
    return (
      <Card withBorder padding="lg" ref={ref}>
        {header}
        <Text fz="sm" c="dimmed" ta="center" py="xs">
          {hasNetwork
            ? "There are no plans to show for your network yet."
            : "Available once your space has a network"}
        </Text>
        {hasNetwork && footer}
      </Card>
    );
  }

  const renderControl = (tier: NetworkPlanTier, index: number) => {
    if (tier.id === data.current_tier_id) {
      // The badge shares the control column with the Switch buttons, so every
      // row has something at the same x and the card reads as one rhythm.
      return <Badge variant="filled" size="sm" fullWidth={!narrow}>Your plan</Badge>;
    }
    if (!data.can_change) return null;
    const isDowngrade = isUncapped || (currentIndex >= 0 && index < currentIndex);
    if (isDowngrade && cooldownDays > 0) {
      // Never a silent failure: the blocked downgrade shows WHEN it unlocks,
      // rather than a button that does nothing.
      return (
        <Tooltip label={`You moved to a slower plan recently — another downgrade unlocks in ${cooldownDays} ${cooldownDays === 1 ? "day" : "days"}.`}>
          <Button size="compact-xs" variant="outline" fullWidth={!narrow} disabled style={{ pointerEvents: "auto" }}>
            in {cooldownDays} {cooldownDays === 1 ? "day" : "days"}
          </Button>
        </Tooltip>
      );
    }
    return (
      <Button
        size="compact-xs"
        variant="outline"
        fullWidth={!narrow}
        onClick={() => startSwitch(tier)}
        disabled={changeMutation.isPending}
      >
        Switch
      </Button>
    );
  };

  const confirmIsDowngrade =
    confirmTier != null &&
    (isUncapped || (currentIndex >= 0 && tiers.findIndex((t) => t.id === confirmTier.id) < currentIndex));

  return (
    <Card withBorder padding="lg" ref={ref}>
      {header}
      <Stack gap={2}>
        {tiers.map((tier, index) => {
          const current = tier.id === data.current_tier_id;
          const rowStyle = {
            display: "grid",
            gridTemplateColumns: narrow ? "1fr auto" : "1fr 108px 96px",
            columnGap: 10,
            rowGap: narrow ? 6 : 0,
            alignItems: "center",
            padding: "6px 10px",
            borderRadius: 6,
            backgroundColor: current ? "var(--mantine-primary-color-light)" : undefined,
          } as const;
          return (
            <Box key={tier.id} style={rowStyle}>
              {/* Narrow: the speed takes its own full-width row so it can
                  never overrun into the control column. */}
              <Box style={narrow ? { gridColumn: "1 / -1" } : undefined}>
                <TierSpeed tier={tier} current={current} />
              </Box>
              <Group justify={narrow ? "flex-start" : "center"} gap={0} wrap="nowrap">
                {renderControl(tier, index)}
              </Group>
              <Text
                fz="sm"
                ta="right"
                c={tier.price_sek === 0 ? "dimmed" : undefined}
                style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}
              >
                {priceLabel(tier)}
              </Text>
            </Box>
          );
        })}
      </Stack>

      {isUnlimitedTier && (
        <Text fz="xs" c="dimmed" mt="sm">
          You have a special plan — unlimited, no cap applied.
          {data.can_change
            ? " Switching to one of the plans above is a downgrade, so it starts the same 7-day cooldown as any other downgrade."
            : " No changes can be made here."}
        </Text>
      )}

      {isUnconfigured && (
        <Text fz="xs" c="dimmed" mt="sm">
          We don&apos;t have a plan on record for this connection yet.{" "}
          <Anchor component={Link} to={askLink} fz="xs">Ask us about it →</Anchor>
        </Text>
      )}

      {user?.is_tenant_admin && !data.can_change && (
        <Text fz="xs" c="dimmed" mt="sm">
          This connection is shared with another tenant. A change would affect them too, so plan
          changes here go through us —{" "}
          <Anchor component={Link} to={askLink} fz="xs">get in touch</Anchor>.
        </Text>
      )}

      {data.can_change && cooldownDays > 0 && (
        <Text fz="xs" c="dimmed" mt="sm">
          You moved to a slower plan recently. Another downgrade is available in{" "}
          <Text component="span" fz="xs" fw={600}>{cooldownDays} {cooldownDays === 1 ? "day" : "days"}</Text>.
          Upgrades are not affected.
        </Text>
      )}

      {footer}

      {/* Confirmation before any change — names the rate and that it takes
          effect now, so nobody switches on a misread row. */}
      <Modal
        opened={!!confirmTier}
        onClose={() => setConfirmTier(null)}
        title={confirmTier ? `Switch to ${speedLabel(confirmTier)}?` : ""}
      >
        {confirmTier && (
          <Stack gap="md">
            <Text fz="sm">
              {confirmTier.price_sek === 0
                ? "This plan is included in your rent."
                : `This plan costs ${confirmTier.price_sek.toLocaleString("sv-SE")} kr per month, excluding VAT.`}{" "}
              The change takes effect immediately. We record the date; your invoice is handled
              separately.
            </Text>
            {confirmIsDowngrade && (
              <Text fz="sm" c="dimmed">
                Moving to a slower plan starts the 7-day downgrade window — the next downgrade
                will not be available until it passes.
              </Text>
            )}
            <Group justify="flex-end">
              <Button variant="outline" onClick={() => setConfirmTier(null)}>Cancel</Button>
              <Button
                onClick={() => changeMutation.mutate(confirmTier)}
                loading={changeMutation.isPending}
              >
                Switch plan
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Missing billing details: the button opens the billing form rather
          than failing, and the parked intent completes the change on return. */}
      <Modal
        opened={!!billingDetourTier}
        onClose={() => {
          sessionStorage.removeItem(PENDING_PLAN_CHANGE_KEY);
          setBillingDetourTier(null);
        }}
        title="Billing details needed first"
      >
        <Stack gap="md">
          <Text fz="sm">
            Before your first plan change we need your billing details — how your invoice should
            reach you. Add them once in Settings and we'll bring you straight back here to finish
            switching to{" "}
            <Text component="span" fz="sm" fw={600}>
              {billingDetourTier ? speedLabel(billingDetourTier) : ""}
            </Text>.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={() => {
                sessionStorage.removeItem(PENDING_PLAN_CHANGE_KEY);
                setBillingDetourTier(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => navigate("/settings/billing")}>Add billing details</Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
